import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Button, Segmented, Row, Col } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import TaskBoard from './TaskBoard';
import { PageHeaderLayout, PageContentLayout } from '../../../shared/layout';
import { withRouter, useLocation, useHistory, Switch, Route } from 'react-router-dom';
import UnassignedTasksView from './UnassignedTasksView';
import { HasAccess } from '@permify/react-role';
import { useTaskExport } from '../hooks/useTaskExport';
import '../styles/BoardTabs.css';
import TaskOverdueChart from './TaskOverdueChart';
import ProjectBoard from './ProjectBoard';
import { useWindowWidth } from '../../../../core/hooks/useWindowWidth';

// Set Document Title
document.title = `Task Management`;
const { TabPane } = Tabs;

const Task = (props) => {
  const location = useLocation();
  const history = useHistory();
  const { exportTasks, isExporting } = useTaskExport();
  const windowWidth = useWindowWidth();
  // Determine current task filter from URL
  const getCurrentFilter = useCallback(() => {
    if (location.pathname.includes('/tasks/unassigned')) return 'unassigned';
    if (location.pathname.includes('/tasks/overdue')) return 'overdue';
    return 'main';
  }, [location.pathname]);

  const [taskFilter, setTaskFilter] = useState(getCurrentFilter());

  const isPlatformLevel = props.currentUser?.role?.org?.level === 'PLATFORM';

  // Sync filter state when URL changes
  useEffect(() => {
    setTaskFilter(getCurrentFilter());
  }, [getCurrentFilter, location.pathname]);

  // Tab configuration - simplified to 2 tabs for PLATFORM users
  const tabConfig = {
    routes: {
      '1': '/tasks/projects',
      '2': '/tasks/main/1'
    },
    pathMap: {
      '/tasks/projects': '1',
      '/tasks/main': '2',
      '/tasks/unassigned': '2', 
      '/tasks/overdue': '2'
    },
    defaultKey: '1'
  };

  const getActiveKey = () => {
    const currentPath = Object.keys(tabConfig.pathMap).find(path =>
      location.pathname.includes(path)
    );
    return currentPath ? tabConfig.pathMap[currentPath] : tabConfig.defaultKey;
  };

  const handleTabChange = (key) => {
    const route = tabConfig.routes[key] || tabConfig.routes[tabConfig.defaultKey];
    history.push(route);
  };

  const handleFilterChange = (filter) => {
    setTaskFilter(filter);
    // Update URL based on filter selection
    switch(filter) {
      case 'unassigned':
        history.push('/tasks/unassigned/1');
        break;
      case 'overdue':
        history.push('/tasks/overdue/');
        break;
      default:
        history.push('/tasks/main/1');
        break;
    }
  };

  return (
    <>
      <PageHeaderLayout
        title="TASKS"
        style={{
          fontSize: '25px',
          border: 'none',
          paddingTop: '2vh',
        }}
      />
      <PageContentLayout style={{ backgroundColor: 'white', paddingInline: '2%' }}>
        {isPlatformLevel ? (
          <HasAccess roles={['PLATFORM']}>
            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }} align="middle">
              <Col xs={24} sm={24} md={getActiveKey() === '2' ? 12 : 24} lg={getActiveKey() === '2' ? 12 : 24}>
                <Tabs
                  className="custom-tabs-tasks"
                  activeKey={getActiveKey()}
                  onChange={handleTabChange}
                  type="card"
                  tabBarStyle={{
                    border: '4px solid #f1f5f9',
                    borderRadius: '8px',
                    width: 'fit-content',
                    display: 'inline-flex',
                    fontSize: windowWidth < 425 ? '12px' : '14px',
                  }}
                >
                  <TabPane tab="Projects" key="1" />
                  <TabPane tab={windowWidth < 425 ? 'Tasks' : 'General Tasks'} key="2" />
                </Tabs>
              </Col>
              {getActiveKey() === '2' && (
                <Col xs={24} sm={24} md={12} lg={12}>
                  <div style={{ display: 'flex', justifyContent: windowWidth < 768 ? 'flex-start' : 'flex-end', marginTop: windowWidth < 768 ? '10px' : '0' }}>
                    <Button
                      onClick={exportTasks}
                      style={{
                        fontSize: '15px',
                        height: '41px',
                        borderRadius: '5px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        width: windowWidth < 768 ? '100%' : '100%',
                        maxWidth: windowWidth < 768 ? '100%' : '150px',
                      }}
                      icon={<DownloadOutlined />}
                      disabled={isExporting}
                      loading={isExporting}
                    >
                      Export Tasks
                    </Button>
                  </div>
                </Col>
              )}
            </Row>

            {/* Show filter for General Tasks tab */}
            {getActiveKey() === '2' && (
              <Row style={{ marginBottom: '20px' }}>
                <Col xs={24} sm={24}>
                  <Segmented
                    value={taskFilter}
                    onChange={handleFilterChange}
                    options={[
                      { label: 'Task Board', value: 'main' },
                      { label: 'Unassigned', value: 'unassigned' },
                      { label: 'Overdue', value: 'overdue' }
                    ]}
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                    }}
                    block
                  />
                </Col>
              </Row>
            )}

            <Switch>
              <Route path="/tasks/projects" exact component={ProjectBoard} />
              <Route path="/tasks/unassigned/:pageNumber?" exact component={UnassignedTasksView} />
              <Route path="/tasks/overdue/" exact component={TaskOverdueChart} />
              <Route path="/tasks/main/:pageNumber?" exact component={TaskBoard} />
            </Switch>
          </HasAccess>
        ) : (
          <TaskBoard />
        )}
      </PageContentLayout>
    </>
  );
};

export default withRouter(Task);
